<template>
  <!-- Listado de asignaturas de la institución con su estado (is_closed), en
  dos pestañas: Asignaturas de Perfil de Egreso y Asignaturas Base. Solo
  lectura. Usa NewGenericBTable contra los endpoints livianos de dashboard
  /dashboards/egress-profile-matter/ y /dashboards/matter/ : ~10 filas por
  página con los nombres ya resueltos y los conteos por estado
  (open_count/closed_count) en la misma respuesta, sin requests adicionales.
  Los chips filtran cada tabla por estado (por defecto: en proceso). El KPI
  (@count) suma ambos estados de ambas pestañas. -->
  <div class="dashboard-active-matters">
    <Card class="dashboard-card" :hide_title="true">
      <template v-slot:content>
        <b-tabs class="matters-tabs" v-model="tab_index" content-class="mt-3">
          <!-- Asignaturas de Perfil de Egreso -->
          <b-tab
            :title="
              $getVisibleNames('mesh.egressprofilematter', true, 'Asignaturas')
            "
          >
            <!-- Chips de estado: muestran los conteos y alternan el filtro de la tabla. -->
            <div class="matters-summary mb-3">
              <span
                class="kl-chip summary-chip state-chip--open"
                :class="{ 'summary-chip--active': !epm_show_closed }"
                role="button"
                @click="epm_show_closed = false"
              >
                <b-icon icon="unlock-fill" class="mr-1"></b-icon>
                {{ $t("dashboard.directiveDashboard.inProcess") }}:
                <strong class="ml-1">{{ epm_open }}</strong>
              </span>
              <span
                class="kl-chip summary-chip state-chip--closed ml-2"
                :class="{ 'summary-chip--active': epm_show_closed }"
                role="button"
                @click="epm_show_closed = true"
              >
                <b-icon icon="lock-fill" class="mr-1"></b-icon>
                {{ $t("dashboard.directiveDashboard.closed") }}:
                <strong class="ml-1">{{ epm_closed }}</strong>
              </span>
            </div>
            <NewGenericBTable
              :fields="epm_fields"
              :url="'/dashboards/egress-profile-matter/'"
              :pagination="10"
              :view_filter_by="false"
              :display_id="'dashboard-active-matters-epm'"
              :pre_filter_tags="epm_filter_tags"
              :no_local_sorting="true"
              @response="(response) => onResponse('epm', response)"
            >
              <template #cell(is_closed)="row">
                <span
                  class="kl-chip"
                  :class="
                    row.item.is_closed
                      ? 'state-chip--closed'
                      : 'state-chip--open'
                  "
                >
                  <b-icon
                    :icon="row.item.is_closed ? 'lock-fill' : 'unlock-fill'"
                    class="mr-1"
                  ></b-icon>
                  {{
                    row.item.is_closed
                      ? $t("dashboard.directiveDashboard.closed")
                      : $t("dashboard.directiveDashboard.inProcess")
                  }}
                </span>
              </template>
              <!-- Navegación: Programa de la Asignatura + el Perfil de Egreso al
              que pertenece. Solo en esta pestaña; las Asignaturas Base no cuelgan
              de un Perfil de Egreso. -->
              <template #cell(navigation)="row">
                <DashboardNavigationButton
                  v-if="row.item.egress_profile"
                  :id_button="`dashboard-active-matters-nav-${row.item.id}`"
                  :egress_profile_id="row.item.egress_profile"
                  :matter_id="row.item.id"
                ></DashboardNavigationButton>
              </template>
            </NewGenericBTable>
          </b-tab>

          <!-- Asignaturas Base -->
          <b-tab
            :title="
              $getVisibleNames('teaching.matter', true, 'Asignaturas Base')
            "
          >
            <!-- Chips de estado: muestran los conteos y alternan el filtro de la tabla. -->
            <div class="matters-summary mb-3">
              <span
                class="kl-chip summary-chip state-chip--open"
                :class="{ 'summary-chip--active': !base_show_closed }"
                role="button"
                @click="base_show_closed = false"
              >
                <b-icon icon="unlock-fill" class="mr-1"></b-icon>
                {{ $t("dashboard.directiveDashboard.inProcess") }}:
                <strong class="ml-1">{{ base_open }}</strong>
              </span>
              <span
                class="kl-chip summary-chip state-chip--closed ml-2"
                :class="{ 'summary-chip--active': base_show_closed }"
                role="button"
                @click="base_show_closed = true"
              >
                <b-icon icon="lock-fill" class="mr-1"></b-icon>
                {{ $t("dashboard.directiveDashboard.closed") }}:
                <strong class="ml-1">{{ base_closed }}</strong>
              </span>
            </div>
            <NewGenericBTable
              :fields="base_fields"
              :url="'/dashboards/matter/'"
              :pagination="10"
              :view_filter_by="false"
              :display_id="'dashboard-active-matters-base'"
              :pre_filter_tags="base_filter_tags"
              :no_local_sorting="true"
              @response="(response) => onResponse('base', response)"
            >
              <!-- Navegación al Programa de la Asignatura Base: solo iacc (directivos
              institucionales o superusuario), como en OldMatterAltList. -->
              <template #cell(name)="row">
                <b-link
                  v-if="can_navigate_old_matter_program"
                  :to="{
                    name: 'OldMatterProgramView',
                    params: { matter_id: row.item.id },
                  }"
                >
                  {{ row.item.name }}
                </b-link>
                <span v-else>{{ row.item.name }}</span>
              </template>
              <template #cell(is_closed)="row">
                <span
                  class="kl-chip"
                  :class="
                    row.item.is_closed
                      ? 'state-chip--closed'
                      : 'state-chip--open'
                  "
                >
                  <b-icon
                    :icon="row.item.is_closed ? 'lock-fill' : 'unlock-fill'"
                    class="mr-1"
                  ></b-icon>
                  {{
                    row.item.is_closed
                      ? $t("dashboard.directiveDashboard.closed")
                      : $t("dashboard.directiveDashboard.inProcess")
                  }}
                </span>
              </template>
            </NewGenericBTable>
          </b-tab>
        </b-tabs>
      </template>
    </Card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
export default {
  name: "DashboardActiveMatters",
  components: {
    Card: () => import("@/components/users/Card"),
    NewGenericBTable: () => import("@/components/reusable/NewGenericBTable"),
    DashboardNavigationButton: () =>
      import("@/components/users/DashboardCards/DashboardNavigationButton"),
  },
  data() {
    return {
      tab_index: 0,
      // ¿Las filas traen el Perfil de Egreso? Arranca en false para no mostrar
      // una columna de navegación vacía antes de la primera respuesta.
      epm_has_egress_profile: false,
      // Filtro de estado por pestaña (los chips lo alternan).
      epm_show_closed: false,
      base_show_closed: false,
      // Conteos por estado, incluidos en cada respuesta (respetan el search).
      epm_open: 0,
      epm_closed: 0,
      base_open: 0,
      base_closed: 0,
    };
  },
  computed: {
    ...mapGetters({
      user: "getUser",
      institution: "getInstitution",
    }),
    // Solo iacc (directivos institucionales o superusuario) navega al Programa
    // de la Asignatura Base, igual que en OldMatterAltList.
    can_navigate_old_matter_program() {
      return !!(
        this.user &&
        (this.user.is_superuser ||
          (this.user.group_names.includes(
            "DIRECTIVOS ACADÉMICOS INSTITUCIONALES"
          ) &&
            this.institution &&
            this.institution.internal_use_id == "iacc.cl"))
      );
    },
    // Computed para referencia estable: el watcher de la tabla refetchea al cambiar.
    epm_filter_tags() {
      return [`is_closed=${this.epm_show_closed}`];
    },
    base_filter_tags() {
      return [`is_closed=${this.base_show_closed}`];
    },
    // Campos de Asignaturas de Perfil de Egreso. El serializer de dashboard ya
    // trae los nombres resueltos (matter_name, egress_profile_career_name) y el
    // effective_code con fallback al de la matter base.
    epm_fields() {
      const fields = [
        {
          key: "matter_name",
          label: String(
            this.$getVisibleNames(
              "mesh.egressprofilematter",
              false,
              "Asignatura"
            )
          ),
          sortable: true,
          display_column: true,
          tdClass: "text-left",
        },
        {
          // El servidor anota y ordena por este mismo campo, el que muestra la celda.
          key: "effective_code",
          label: String(this.$t("curricularProducts.matterList.code")),
          sortable: true,
          display_column: true,
          tdClass: "text-left",
        },
        {
          key: "plan_level",
          label: String(this.$t("curricularProducts.matterList.planLevel")),
          sortable: true,
          display_column: true,
          tdClass: "text-center",
        },
        {
          key: "egress_profile_career_name",
          label: String(
            this.$getVisibleNames(
              "mesh.egressprofile",
              false,
              "Perfil de Egreso"
            )
          ),
          sortable: true,
          display_column: true,
          tdClass: "text-left",
        },
        {
          key: "is_closed",
          label: String(this.$t("dashboard.directiveDashboard.state")),
          sortable: false,
          display_column: true,
          thStyle: { width: "14%" },
        },
      ];
      // Sin el Perfil de Egreso en la fila no hay a dónde navegar: se omite la
      // columna entera en vez de dejar celdas vacías.
      if (this.epm_has_egress_profile)
        fields.push({
          key: "navigation",
          label: String(this.$t("dashboard.directiveDashboard.navigation")),
          sortable: false,
          display_column: true,
          thStyle: { width: "8%" },
        });
      return fields;
    },
    base_fields() {
      return [
        {
          key: "name",
          label: String(
            this.$getVisibleNames("teaching.matter", false, "Asignatura Base")
          ),
          sortable: true,
          display_column: true,
          tdClass: "text-left",
        },
        {
          key: "code",
          label: String(this.$t("curricularProducts.matterList.code")),
          sortable: true,
          display_column: true,
          tdClass: "text-left",
        },
        {
          key: "weeks",
          label: String(this.$t("curricularProducts.matterList.weeks")),
          sortable: true,
          display_column: true,
          tdClass: "text-center",
        },
        {
          key: "is_closed",
          label: String(this.$t("dashboard.directiveDashboard.state")),
          sortable: false,
          display_column: true,
          thStyle: { width: "14%" },
        },
      ];
    },
  },
  methods: {
    // Conteos desde la misma respuesta paginada; el KPI suma ambos estados de
    // ambas pestañas.
    onResponse(type, response) {
      this[`${type}_open`] = response.open_count || 0;
      this[`${type}_closed`] = response.closed_count || 0;
      if (type == "epm")
        this.epm_has_egress_profile = (response.results || []).some(
          (egress_profile_matter) =>
            egress_profile_matter.egress_profile != null
        );
      this.$emit(
        "count",
        this.epm_open + this.epm_closed + this.base_open + this.base_closed
      );
    },
  },
};
</script>

<style scoped>
/* Marco de la tarjeta: mismo estándar que DirectiveDashboard/DashboardEgressProfile. */
.dashboard-card {
  display: block;
  margin: 0;
}
.dashboard-card >>> .card-container {
  background: var(--kl-surface);
  border: 1px solid var(--kl-border-soft);
  border-radius: var(--kl-card-radius);
  box-shadow: 0 2px 4px rgba(16, 38, 73, 0.06);
  overflow: hidden;
}
.dashboard-card >>> .card-body {
  background: var(--kl-surface) !important;
  padding: 16px;
}
.dashboard-card >>> .b-table-container {
  margin: 0;
}
/* Pestañas alineadas al estándar KL (azul de marca en la activa). */
.matters-tabs >>> .nav-tabs .nav-link {
  color: var(--kl-text-muted);
  font-weight: var(--kl-fw-semibold);
  border: none;
}
.matters-tabs >>> .nav-tabs .nav-link.active {
  color: var(--kl-color-primary);
  border-bottom: 2px solid var(--kl-color-primary);
  background: transparent;
}
/* Resumen de totales por estado, arriba de cada tabla. */
.matters-summary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
/* Chips clickeables: filtran la tabla por estado (el activo a opacidad completa). */
.summary-chip {
  font-size: var(--kl-fs-small);
  padding: 0.3rem 0.7rem;
  cursor: pointer;
  user-select: none;
  opacity: 0.55;
}
.summary-chip--active {
  opacity: 1;
  outline: 1px solid currentColor;
}
/* Chip de estado (sobre el estándar .kl-chip). */
.state-chip--closed {
  background: rgba(214, 69, 80, 0.12);
  color: var(--kl-color-danger);
  border-color: rgba(214, 69, 80, 0.35);
  font-weight: var(--kl-fw-semibold);
}
.state-chip--open {
  background: rgba(46, 158, 91, 0.12);
  color: var(--kl-color-success);
  border-color: rgba(46, 158, 91, 0.4);
  font-weight: var(--kl-fw-semibold);
}
</style>
